import {
  CheckCircleOutlined as CheckCircleOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassTop as HourglassTopIcon,
  Undo as UndoIcon,
  InfoOutlined as InfoOutlinedIcon,
  East as EastIcon,
} from '@mui/icons-material';
import {
  SvgIconPropsColorOverrides,
  SvgIconTypeMap,
  Tooltip,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { OverridableStringUnion } from '@mui/types';
import { find } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { payoutRequisitesBalanceRedistributionRequestsApi } from 'api';
import {
  CopyText,
  CopyTextId,
  CrudPage,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { PayoutOrderAutomationStatus, QueryKey } from 'enums';
import { useMoney, usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  PayoutRequisitesBalanceRedistribution,
  PayoutRequisitesBalanceRedistributionRequest,
} from 'types';
import { formatUtils } from 'utils';

export const PayoutRequisitesBalanceRedistributionList: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.payout_requisites_balance_redistribution',
  });
  const { getFormattedMoney } = useMoney();

  const queryResult = usePartialQuery(
    QueryKey.PayoutRequisitesBalanceRedistribution,
    payoutRequisitesBalanceRedistributionRequestsApi.getAllPaginated,
  );

  const renderIcon = useCallback(
    (redistribution: PayoutRequisitesBalanceRedistribution) => {
      let iconColor: OverridableStringUnion<
        | 'inherit'
        | 'action'
        | 'disabled'
        | 'primary'
        | 'secondary'
        | 'error'
        | 'info'
        | 'success'
        | 'warning',
        SvgIconPropsColorOverrides
      > = 'disabled';
      let IconComponent: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
        muiName: string;
      } = InfoOutlinedIcon;

      if (redistribution.status === PayoutOrderAutomationStatus.Completed) {
        iconColor = 'success';
        IconComponent = CheckCircleOutlinedIcon;
      } else if (redistribution.status === PayoutOrderAutomationStatus.Error) {
        iconColor = 'error';
        IconComponent = ErrorOutlineIcon;
      } else if (
        redistribution.status === PayoutOrderAutomationStatus.InProgress
      ) {
        iconColor = 'disabled';
        IconComponent = HourglassTopIcon;
      } else if (
        redistribution.status === PayoutOrderAutomationStatus.Rejected
      ) {
        iconColor = 'disabled';
        IconComponent = UndoIcon;
      }
      return (
        <Tooltip
          title={
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div>{`${t('columns.id')}: ${redistribution.id}`}</div>
              {redistribution.paymentId && (
                <div>
                  {`${t('columns.payment_id')}: ${redistribution.paymentId}`}
                </div>
              )}
              {redistribution.error && <div>{redistribution.error}</div>}
            </div>
          }
        >
          <IconComponent color={iconColor} fontSize="small" sx={{ mr: 1 }} />
        </Tooltip>
      );
    },
    [t],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<PayoutRequisitesBalanceRedistributionRequest>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('columns.user'),
        valueGetter: (item) => (
          <div>
            <div>{item.user?.name}</div>
            <CopyTextId id={item.user?.id!} />
          </div>
        ),
      },
      {
        header: t('columns.max_balance'),
        valueGetter: (item) =>
          getFormattedMoney({
            value: item.maxBalance,
            fiatCurrencyId: item.balanceFiatCurrencyId,
            symbol: true,
          }),
      },
      {
        header: t('columns.from_automations'),
        multiValueRenderer: {
          itemsGetter: (item) => item.fromAutomations,
          valueGetter: (automation) => (
            <div
              key={automation.id}
              className="tw-flex tw-items-center tw-gap-1"
            >
              {automation?.name}
              <CopyText text={automation.id} iconOnly />
            </div>
          ),
        },
      },
      {
        header: t('columns.to_automations'),
        multiValueRenderer: {
          itemsGetter: (item) => item.toAutomations,
          valueGetter: (automation) => (
            <div
              key={automation.id}
              className="tw-flex tw-items-center tw-gap-1"
            >
              {automation?.name}
              <CopyText text={automation.id} iconOnly />
            </div>
          ),
        },
      },
      {
        header: t('columns.operations'),
        multiValueRenderer: {
          itemsGetter: (item) => item.redistributions,
          valueGetter: (
            redistribution: PayoutRequisitesBalanceRedistribution,
            item,
          ) => (
            <div
              key={redistribution.id}
              className="[&:not(:last-child)]:tw-mb-1"
            >
              <div className="tw-flex tw-flex-wrap">
                <div className="tw-flex">{renderIcon(redistribution)}</div>
                <div>
                  {
                    find(
                      item.fromAutomations,
                      (automation) =>
                        automation.id === redistribution.fromAutomationId,
                    )?.name
                  }
                </div>
                <EastIcon fontSize="small" color="disabled" sx={{ mx: 1 }} />
                <div>
                  {
                    find(
                      item.toAutomations,
                      (automation) =>
                        automation.id === redistribution.toAutomationId,
                    )?.name
                  }
                </div>
                <div className="tw-ml-0.5">
                  {`(${getFormattedMoney({
                    value: redistribution.amount,
                    fiatCurrencyId: item.balanceFiatCurrencyId,
                    symbol: true,
                  })})`}
                </div>
              </div>
            </div>
          ),
        },
      },
      {
        header: t('columns.completed_at'),
        valueGetter: (item) => formatUtils.formatDate(item.completedAt),
      },
    ],
    [t, getFormattedMoney, renderIcon],
  );

  return (
    <CrudPage
      table={{
        queryResult,
        paginated: true,
        columns,
      }}
    />
  );
};
